import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Box, Typography } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { AutoCompleteSelectUI, DatePickerUI, FormErrorUI, FormLabelUI, InputUI } from '../../../Interface';
import useParam from '../../../../context/ParamContext';
import companyService from '../../../../service/company.service';
import { generateAutocompleteItems } from '../../../../utils/helper';

const CreateWorkspaceDialogFields = ({ autoCompleteOptions, autoCompleteData }) => {
  const { params } = useParam();
  const { values, setFieldValue, errors, validateField } = useFormikContext();
  const [companyContacts, setCompanyContacts] = useState([]);
  const [companyContactOptions, setCompanyContactOptions] = useState([]);

  const showProjectTypeStatus = params.PROJECT_SHOW_PROJECT_TYPE_STATUS === 'Y';

  const statusFieldName = showProjectTypeStatus ? 'projectTypeStatusId' : 'statusId';

  const filteredStatusOptions = useMemo(() => {
    if (!showProjectTypeStatus) {
      return autoCompleteOptions.status;
    }

    const statusForProjectType = autoCompleteData?.status?.filter(
      (status) => status.projectTypeId === values.projectTypeId
    );

    return generateAutocompleteItems(statusForProjectType, 'name', 'id');
  }, [values.projectTypeId, params]);

  useEffect(() => {
    loadCompanyContacts();
  }, [values.companyId]);

  const loadCompanyContacts = async () => {
    await setFieldValue('companyContactId', null);
    await setFieldValue('companyContactRole', '');
    await setFieldValue('companyContactPhoneNumber', '');
    await setFieldValue('companyContactEmail', '');
    setCompanyContacts([]);

    if (!values.companyId) return;

    const company = await companyService.getCompanyById(values.companyId);

    const contacts = company?.companyContact ?? [];

    setCompanyContacts(contacts);
    setCompanyContactOptions(generateAutocompleteItems(contacts, 'name', 'id'));

    const defaultContact = contacts.find((contact) => contact.default);

    await setFieldValue('companyContactId', defaultContact?.id ?? null);
    await setFieldValue('companyContactRole', defaultContact?.role ?? '');
    await setFieldValue('companyContactPhoneNumber', defaultContact?.phoneNumber ?? '');
    await setFieldValue('companyContactEmail', defaultContact?.email ?? '');
  };

  const handleCompanyContactChange = async (_event, newValue) => {
    const contactId = newValue?.value ?? null;

    const selectedContact = companyContacts.find((contact) => contact.id === contactId) ?? null;

    await setFieldValue('companyContactId', contactId);
    await setFieldValue('companyContactRole', selectedContact?.role ?? '');
    await setFieldValue('companyContactPhoneNumber', selectedContact?.phoneNumber ?? '');
    await setFieldValue('companyContactEmail', selectedContact?.email ?? '');

    await validateField('companyContactId');
  };

  return (
    <>
      <Box
        sx={{
          background: '#E9F5FE',
          borderRadius: '6px',
          px: 2,
          pb: 1.5,
          mb: 2,
        }}
      >
        <Grid container spacing={2}>
          {showProjectTypeStatus && (
            <Grid item xs={12} md={4}>
              <Field
                type="autocomplete"
                name="projectTypeId"
                component={AutoCompleteSelectUI}
                fullWidth
                label={<FormLabelUI text="Projekt típus" />}
                size="small"
                variant="outlined"
                helperText={<FormErrorUI message={errors.projectTypeId} />}
                onChange={(_e, newVal) => {
                  setFieldValue('projectTypeId', newVal?.value ?? null).then(() => {
                    validateField('projectTypeId');
                  });
                  setFieldValue('projectTypeStatusId', null);
                }}
                InputLabelProps={{ shrink: values.projectTypeId !== '' }}
                selectedValue={values.projectTypeId}
                dataset={autoCompleteOptions.projectType}
              />
            </Grid>
          )}
          <Grid item xs={12} md={showProjectTypeStatus ? 4 : 6}>
            <Field
              type="autocomplete"
              name={statusFieldName}
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Státusz" />}
              size="small"
              variant="outlined"
              helperText={<FormErrorUI message={errors[statusFieldName]} />}
              onChange={(_e, newVal) => {
                setFieldValue(statusFieldName, newVal?.value ?? null).then(() => {
                  validateField(statusFieldName);
                });
              }}
              InputLabelProps={{ shrink: values[statusFieldName] !== '' }}
              selectedValue={values[statusFieldName]}
              dataset={filteredStatusOptions}
            />
          </Grid>
          <Grid item xs={12} md={showProjectTypeStatus ? 4 : 6}>
            <Field
              type="autocomplete"
              name="responsiblePersonId"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Felelős" />}
              size="small"
              variant="outlined"
              helperText={<FormErrorUI message={errors.responsiblePersonId} />}
              onChange={(_e, newVal) => {
                setFieldValue('responsiblePersonId', newVal?.value ?? null).then(() => {
                  validateField('responsiblePersonId');
                });
              }}
              InputLabelProps={{ shrink: values.responsiblePersonId !== '' }}
              selectedValue={values.responsiblePersonId}
              dataset={autoCompleteOptions.user}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Field
              type="autocomplete"
              name="companyLocationId"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Telephely" />}
              size="small"
              variant="outlined"
              helperText={<FormErrorUI message={errors.companyLocationId} />}
              onChange={(_e, newVal) => {
                setFieldValue('companyLocationId', newVal?.value ?? null).then(() => {
                  validateField('companyLocationId');
                });
              }}
              InputLabelProps={{ shrink: values.companyLocationId !== '' }}
              selectedValue={values.companyLocationId}
              dataset={autoCompleteOptions.companyLocation}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Field
              type="date"
              name="projectStartDate"
              component={DatePickerUI}
              value={values.projectStartDate}
              fullWidth
              label={<FormLabelUI text="Kezdő dátum" />}
              size="small"
              variant="outlined"
              helperText={<FormErrorUI message={errors.projectStartDate} />}
              onChange={(date) => {
                setFieldValue('projectStartDate', date).then(() => {
                  validateField('projectStartDate');
                });
              }}
              InputLabelProps={{ shrink: values.projectStartDate !== '' }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Field
              type="date"
              name="projectEndDate"
              component={DatePickerUI}
              value={values.projectStartDate}
              fullWidth
              label={<FormLabelUI text="Befejező dátum" />}
              size="small"
              variant="outlined"
              helperText={<FormErrorUI message={errors.projectEndDate} />}
              onChange={(date) => {
                setFieldValue('projectEndDate', date).then(() => {
                  validateField('projectEndDate');
                });
              }}
              InputLabelProps={{ shrink: values.projectEndDate !== '' }}
            />
          </Grid>
        </Grid>
      </Box>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              background: '#E9F5FE',
              borderRadius: '6px',
              p: '1rem',
              height: '100%',
            }}
          >
            <Typography fontWeight="bold" mb={2}>
              Cég
            </Typography>
            <Field
              type="autocomplete"
              name="companyId"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Cég" />}
              size="small"
              variant="outlined"
              helperText={<FormErrorUI message={errors.companyId} />}
              onChange={(_e, newVal) => {
                setFieldValue('companyId', newVal?.value ?? null).then(() => {
                  validateField('companyId');
                });
              }}
              InputLabelProps={{ shrink: values.companyId !== '' }}
              selectedValue={values.companyId}
              dataset={autoCompleteOptions.company}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              background: '#E9F5FE',
              borderRadius: '6px',
              p: '1rem',
            }}
          >
            <Typography fontWeight="bold" mb={2}>
              Fő kapcsolattartó
            </Typography>
            <Box mb={1.5}>
              <Field
                type="autocomplete"
                name="companyContactId"
                component={AutoCompleteSelectUI}
                fullWidth
                label={<FormLabelUI text="Név" />}
                size="small"
                variant="outlined"
                helperText={<FormErrorUI message={errors.companyContactId} />}
                onChange={handleCompanyContactChange}
                InputLabelProps={{ shrink: values.companyContactId !== '' }}
                selectedValue={values.companyContactId}
                dataset={companyContactOptions}
              />
            </Box>
            <Box mb={1.5}>
              <Field
                type="text"
                name="companyContactRole"
                as={InputUI}
                fullWidth
                label={<FormLabelUI text="Szerep" />}
                size="small"
                helperText={<FormErrorUI message={errors.companyContactRole} />}
                InputLabelProps={{ shrink: values.companyContactRole !== '' }}
                disabled
              />
            </Box>
            <Box mb={1.5}>
              <Field
                type="text"
                name="companyContactPhoneNumber"
                as={InputUI}
                fullWidth
                label={<FormLabelUI text="Telefonszám" />}
                size="small"
                helperText={<FormErrorUI message={errors.companyContactPhoneNumber} />}
                InputLabelProps={{ shrink: values.companyContactPhoneNumber !== '' }}
                disabled
              />
            </Box>
            <Box mb={1.5}>
              <Field
                type="text"
                name="companyContactEmail"
                as={InputUI}
                fullWidth
                label={<FormLabelUI text="Email" />}
                size="small"
                helperText={<FormErrorUI message={errors.companyContactEmail} />}
                InputLabelProps={{ shrink: values.companyContactEmail !== '' }}
                disabled
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{
          background: '#E9F5FE',
          borderRadius: '6px',
          p: 2,
        }}
      >
        <Field
          type="text"
          name="projectName"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Adatlap neve" />}
          size="small"
          helperText={<FormErrorUI message={errors.projectName} />}
          InputLabelProps={{ shrink: values.projectName !== '' }}
        />
      </Box>
    </>
  );
};

export default CreateWorkspaceDialogFields;
